import * as React from "react";
import { createContext, FunctionComponent, useCallback, useEffect, useState } from "react";
import { isNil } from "lodash";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { DefaultButton, Spinner, SpinnerSize } from "@fluentui/react";
import StatusHeader from "./shared/status-header/StatusHeader";
import * as Sentry from "@sentry/react";
import { useAccount, useMsal } from "@azure/msal-react";
import { useEmployeeLazyQuery, useLoginWithMicrosoftAddinTokenMutation, useViewerQuery } from "@graphql";
import RegionSelector from "./shared/region-selector/RegionSelector";
import { getCurrentUrl, getLocalStorageRegion, getQueryParams } from "@shared";

/* global OfficeRuntime */

export interface Props {
  children: React.ReactNode;
}

export interface User {
  _id: string;
  id?: string;
  idToken: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  mobile?: string;
  customerId?: string;
  userHash?: string;
  picture?: string;
  jobTitle?: string;
  homeLocation?: {
    _id: string;
  };
}

export const UserContext = createContext<User | null>(null);

const Auth: FunctionComponent<Props> = (props: Props) => {
  const [token, setToken] = useState<string>();
  const [fallbackToken, setFallbackToken] = useState<string>();
  const [authenticationError, setAuthenticationError] = useState<unknown>();
  const [user, setUser] = useState<User>();
  const { children } = props;
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const [showLoginButton, setShowLoginButton] = useState<boolean>(false);
  const [isFallBackAuth, setIsFallBackAuth] = useState<boolean>(false);
  const { data: userDatas, refetch: refetchUserLoggedIn } = useViewerQuery({
    fetchPolicy: "cache-and-network",
  });

  const [getUserEmployeeData, { data: userEmployeeData }] = useEmployeeLazyQuery();

  const setupLocalStorage = ({ refreshToken, idToken, tenant }) => {
    localStorage.setItem("refreshToken", refreshToken);
    localStorage.setItem("token", idToken);
    localStorage.setItem("tenant", tenant);
  };

  const [getLoginMicrosoftAddin, { data: loginData, loading, error }] = useLoginWithMicrosoftAddinTokenMutation({
    onCompleted: async (data) => {
      const { tenant, refreshToken, idToken, userHash } = data.loginWithMicrosoftAddinToken;
      setupLocalStorage({ tenant, refreshToken, idToken });
      refetchUserLoggedIn();
      const user = { ...userDatas.viewer, userHash, idToken };
      setUser(user);
    },
    onError: (error) => {
      setAuthenticationError(JSON.stringify(error));
    },
  });

  const authenticationRedirect = useCallback(() => {
    instance.loginRedirect({ scopes: ["User.Read"], prompt: "select_account" }).catch((error) => {
      console.error(error);
      setAuthenticationError(JSON.stringify(error));
    });
  }, [instance]);

  const tryToAcquireToken = useCallback(() => {
    if (!isNil(account)) {
      instance
        .acquireTokenSilent({
          scopes: ["User.Read"],
          account: account,
        })
        .then((response) => {
          if (response) {
            const token = response.idToken;
            setFallbackToken(token);
            getLoginMicrosoftAddin({ variables: { accessCode: token } });
          }
        })
        .catch((error) => {
          setAuthenticationError(error);
          setShowLoginButton(true);
          if (error instanceof InteractionRequiredAuthError) {
            authenticationRedirect();
          }
        });
    }
  }, [account, authenticationRedirect, getLoginMicrosoftAddin, instance]);

  useEffect(() => {
    if (user?._id) getUserEmployeeData({ variables: { employeeId: user?._id } });
  }, [user]);

  useEffect(() => {
    if (userEmployeeData?.employee?.jobTitle) {
      user.jobTitle = userEmployeeData?.employee?.jobTitle;
      setUser({ ...user });
    }
  }, [userEmployeeData]);

  useEffect(() => {
    try {
      console.log("attempting office auth.");
      OfficeRuntime.auth
        .getAccessToken({ allowSignInPrompt: true, allowConsentPrompt: true })
        .then((authToken) => {
          setToken(authToken);
          getLoginMicrosoftAddin({
            variables: { accessCode: authToken },
          });
        })
        .catch((error) => {
          console.error(error);
          setAuthenticationError(JSON.stringify(error));
        });
    } catch (exception) {
      console.error(JSON.stringify(exception));
      Sentry.captureException(exception);
      console.log("attempting fallback auth.");
      setIsFallBackAuth(true);
      tryToAcquireToken();
    }
  }, [getLoginMicrosoftAddin, tryToAcquireToken]);

  useEffect(() => {
    if (!loading && userDatas) {
      const newUser = { ...user, ...userDatas.viewer };
      setUser(newUser);
    }
  }, [userDatas, loading]);

  useEffect(() => {
    if (!isNil(authenticationError)) {
      const details = {
        Account: JSON.stringify(account),
        Office_Token: token,
        Fallback_Token: fallbackToken,
        User_Details: JSON.stringify(user),
        Error: authenticationError,
        GetTokenError: error?.message,
        CurrentUrl: getCurrentUrl(),
      };
      console.error("Authentication Error Occurred", details);
    }
  }, [authenticationError]);

  const isLoading = loading;
  const isError = !isNil(error) || !isNil(authenticationError);

  return (
    <>
      {isLoading && (
        <>
          {isFallBackAuth && <StatusHeader text={"Attempting fallback authentication"} />}
          {!isFallBackAuth && <StatusHeader text={"Loading..."} isLoading={true} />}
        </>
      )}
      {!isLoading && !isError && !isNil(user) && <UserContext.Provider value={user}>{children}</UserContext.Provider>}
      {showLoginButton && <DefaultButton text="Login" onClick={authenticationRedirect} />}
      {!getLocalStorageRegion() && <RegionSelector />}
      {isError && getLocalStorageRegion() && (
        <div>
          <div style={{ marginTop: 10, textAlign: "center", color: "red" }}>
            <div style={{ marginBottom: 3 }}>An error occurred while attempting to fetch the data!</div>
            <div>Check your selected region.</div>
          </div>
          <div style={{ marginTop: 50 }}>
            <RegionSelector />
          </div>
        </div>
      )}
    </>
  );
};

export default Auth;
